import { styled } from "goober";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../components/Button";
import { FooterAction } from "../components/FooterAction";
import { Icon } from "../components/Icon";
import { Text } from "../components/Text";
import { Tooltip } from "../components/Tooltip";
import { VoiceRecorder } from "../components/VoiceRecorder";
import { maybeDisplayBrowserNotification } from "../lib/browserNotifications";
import { isEditableTarget } from "../lib/isEditableTarget";
import { playClick } from "../lib/sounds";
import type { Answer, AskQuestionItem } from "./AskQuestion";

const PromptContainer = styled("div")`
  border-radius: 12px;
  outline: 0.5px solid var(--border-alpha);
  background: var(--surface-primary);
  box-shadow: var(--elevation-low);
  padding: 4px;
`;

const QuestionHeader = styled("div")`
  display: flex;
  padding: 10px 8px;
  box-sizing: content-box;
  align-items: baseline;
  color: var(--text-secondary);
`;

const PromptBody = styled("div")`
  display: flex;
  flex-direction: column;
  padding-bottom: 4px;
  gap: 4px;
  max-height: 200px;
  overflow-y: auto;
  @media (max-width: 768px) {
    max-height: 40dvh;
  }
`;

const Row = styled("div")`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;
  padding-right: 28px;
  cursor: pointer;
  border-radius: 8px;
  position: relative;
  &[data-highlighted="true"] {
    background: var(--alpha4);
  }
`;

const KeyHint = styled("span")`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background: var(--alpha4);
  flex-shrink: 0;
`;

const ChoiceBadge = styled("span")`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  flex-shrink: 0;
  box-sizing: border-box;
  border: 0.5px solid var(--alpha8);

  &[data-selected="true"] {
    background: var(--control-bg);
    border-color: var(--control-bg);
  }
`;

const OtherInput = styled("input")`
  flex: 1;
  border: 0;
  background: transparent;
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 350;
  font-family: InterVariable;
  line-height: 16px;
  letter-spacing: -0.01em;
  outline: none;
  padding: 0;

  &::placeholder {
    color: var(--text-tertiary);
    opacity: 0.4;
  }
`;

const FreeformGroup = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px 8px 8px;
`;

const InputWrapper = styled("div")`
  position: relative;
`;

const FreeformInput = styled("textarea")`
  display: block;
  padding: 8px 12px;
  padding-right: 48px;
  background-color: var(--surface-primary);
  color: var(--text-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 8px;
  border: 0;
  font-size: 12px;
  font-family: InterVariable;
  line-height: 16px;
  height: 88px;
  overflow-y: auto;
  width: 100%;
  box-sizing: border-box;
  resize: none;

  &::placeholder {
    opacity: 0.3;
  }

  &:focus {
    outline: 0.5px solid var(--control-bg);
    box-shadow: 0 0 0 3px var(--border-alpha);
    &::placeholder {
      opacity: 0.6;
    }
  }
`;

const InputButtons = styled("div")`
  position: absolute;
  right: 8px;
  bottom: 8px;
`;

const RecorderContainer = styled("div")`
  position: relative;
  padding: 8px 12px;
  padding-right: 68px;
  background-color: var(--surface-primary);
  outline: 0.5px solid var(--control-bg);
  box-shadow: 0 0 0 3px var(--border-alpha);
  border-radius: 8px;
  height: 88px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
`;

const Footer = styled("div")`
  display: flex;
  align-items: center;
  margin: 0 -4px -4px -4px;
  height: 40px;
  padding: 0 12px;
  border-top: 0.5px solid var(--border-alpha);
  justify-content: space-between;
`;

const Keyboard = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  padding: 2px;
  background: var(--alpha4);
  color: var(--text-secondary);
`;

const Instructions = styled("div")`
  display: flex;
  align-items: center;
  color: var(--text-secondary);
  gap: 4px;
`;

type Draft = { selected?: string[]; text?: string; otherText?: string };

type Props = {
  questions: AskQuestionItem[];
  onSubmit: (output: unknown) => void;
  onDismiss: () => void;
  onTypeInstead: () => void;
};

export function AskQuestionPrompt({
  questions,
  onSubmit,
  onDismiss,
  onTypeInstead,
}: Props) {
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});

  useEffect(() => {
    maybeDisplayBrowserNotification(
      "3B needs you",
      questions[0]?.question ?? "Answer a question"
    );
  }, [questions]);

  const current = questions[questionIndex];
  if (!current) return null;

  const saveDraft = (draft: Draft) => {
    setDrafts((prev) => ({ ...prev, [current.id]: draft }));
  };

  const navigate = (next: number) => {
    setQuestionIndex(next);
  };

  return (
    <SingleQuestion
      key={current.id}
      question={current}
      draft={drafts[current.id]}
      step={
        questions.length > 1
          ? { current: questionIndex + 1, total: questions.length }
          : undefined
      }
      onAnswer={(answer) => {
        const next = { ...answers, [current.id]: answer };
        setAnswers(next);
        if (questionIndex < questions.length - 1) {
          setQuestionIndex(questionIndex + 1);
        } else {
          onSubmit({ answers: next });
        }
      }}
      onDismiss={onDismiss}
      onTypeInstead={onTypeInstead}
      onPrev={questionIndex > 0 ? () => navigate(questionIndex - 1) : undefined}
      onNext={
        questionIndex < questions.length - 1
          ? () => navigate(questionIndex + 1)
          : undefined
      }
      onDraftChange={saveDraft}
    />
  );
}

function SingleQuestion({
  question: q,
  step,
  draft,
  onAnswer,
  onDismiss,
  onTypeInstead,
  onPrev,
  onNext,
  onDraftChange,
}: {
  question: AskQuestionItem;
  step?: { current: number; total: number };
  draft?: Draft;
  onAnswer: (answer: Answer) => void;
  onDismiss: () => void;
  onTypeInstead: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  onDraftChange?: (draft: Draft) => void;
}) {
  const { options, allowMultiple } = q;
  const hasOptions = options && options.length > 0;
  const isFreeform = !hasOptions;
  const needsExplicitSubmit = allowMultiple || isFreeform;

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(draft?.selected ?? [])
  );
  const [freeformText, setFreeformText] = useState(draft?.text ?? "");
  const [otherActive, setOtherActive] = useState(
    () => draft?.otherText != null && draft.otherText.length > 0
  );
  const [otherText, setOtherText] = useState(draft?.otherText ?? "");
  const [isRecording, setIsRecording] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const otherInputRef = useRef<HTMLInputElement>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);

  const otherIndex = hasOptions ? options.length : 0;
  const totalItems = hasOptions ? options.length + 1 : 0;

  useEffect(() => {
    if (isFreeform && !isRecording) inputRef.current?.focus();
  }, [isFreeform, isRecording]);

  const canSubmit = useCallback(() => {
    if (otherActive)
      return (
        otherText.trim().length > 0 || (allowMultiple && selected.size > 0)
      );
    if (hasOptions && selected.size === 0) return false;
    if (isFreeform && freeformText.trim().length === 0) return false;
    return true;
  }, [
    allowMultiple,
    hasOptions,
    isFreeform,
    selected,
    freeformText,
    otherActive,
    otherText,
  ]);

  const buildAnswer = useCallback((): Answer => {
    if (otherActive) {
      const text = otherText.trim();
      return allowMultiple && selected.size > 0
        ? { selected: [...selected], text }
        : { text };
    }
    if (hasOptions) return { selected: [...selected] };
    return { text: freeformText.trim() };
  }, [
    allowMultiple,
    hasOptions,
    selected,
    freeformText,
    otherActive,
    otherText,
  ]);

  const saveDraft = useCallback(() => {
    onDraftChange?.({
      selected: [...selected],
      text: freeformText,
      otherText: otherActive ? otherText : undefined,
    });
  }, [onDraftChange, selected, freeformText, otherActive, otherText]);

  const handleSubmit = useCallback(() => {
    if (!canSubmit()) return;
    onAnswer(buildAnswer());
  }, [canSubmit, onAnswer, buildAnswer]);

  const handleSelectOption = useCallback(
    (id: string) => {
      if (!allowMultiple) setOtherActive(false);
      if (needsExplicitSubmit) {
        setSelected((prev) => {
          if (allowMultiple) {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
          }
          return prev.has(id) ? new Set() : new Set([id]);
        });
      } else {
        onAnswer({ selected: [id] });
      }
    },
    [allowMultiple, needsExplicitSubmit, onAnswer]
  );

  const handleActivateOther = useCallback(() => {
    setOtherActive(true);
    if (!allowMultiple) setSelected(new Set());
    setTimeout(() => otherInputRef.current?.focus(), 0);
  }, [allowMultiple]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isRecording) return;

      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        if (e.shiftKey) {
          onTypeInstead();
          return;
        }
        if (otherActive) {
          setOtherActive(false);
          return;
        }
        onDismiss();
        return;
      }

      if (e.key === "ArrowLeft" && e.metaKey && e.ctrlKey && onPrev) {
        e.preventDefault();
        saveDraft();
        onPrev();
        return;
      }
      if (e.key === "ArrowRight" && e.metaKey && e.ctrlKey && onNext) {
        e.preventDefault();
        saveDraft();
        onNext();
        return;
      }

      if (isEditableTarget(e)) {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          handleSubmit();
        }
        return;
      }

      if (hasOptions) {
        const numIdx = Number(e.key) - 1;
        if (numIdx >= 0 && numIdx < options.length) {
          e.preventDefault();
          setSelectedIndex(numIdx);
          handleSelectOption(options[numIdx]!.id);
        } else if (numIdx === options.length) {
          e.preventDefault();
          setSelectedIndex(otherIndex);
          handleActivateOther();
        } else if (e.key === "ArrowDown") {
          e.preventDefault();
          e.stopPropagation();
          setSelectedIndex((index) => Math.min(index + 1, totalItems - 1));
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          e.stopPropagation();
          setSelectedIndex((index) => Math.max(index - 1, 0));
        } else if (e.key === "Enter") {
          e.preventDefault();
          e.stopPropagation();
          if (selectedIndex === otherIndex) {
            handleActivateOther();
          } else if (needsExplicitSubmit) {
            handleSubmit();
          } else {
            const option = options[selectedIndex];
            if (option) handleSelectOption(option.id);
          }
        } else if (e.key === " " && needsExplicitSubmit) {
          e.preventDefault();
          if (selectedIndex === otherIndex) {
            handleActivateOther();
          } else {
            const option = options[selectedIndex];
            if (option) handleSelectOption(option.id);
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    handleActivateOther,
    handleSelectOption,
    handleSubmit,
    hasOptions,
    isRecording,
    needsExplicitSubmit,
    onDismiss,
    onTypeInstead,
    onNext,
    onPrev,
    options,
    otherActive,
    otherIndex,
    saveDraft,
    selectedIndex,
    totalItems,
  ]);

  useEffect(() => {
    rowRefs.current[selectedIndex]?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  return (
    <PromptContainer>
      <PromptBody>
        <QuestionHeader>
          {step && (
            <Text
              variant="text-small-md"
              color="var(--text-primary)"
              style={{ marginRight: 4 }}
            >
              {step.current}/{step.total}
            </Text>
          )}
          <Text variant="text-small">{q.question}</Text>
        </QuestionHeader>
        {hasOptions &&
          options.map((option, index) => (
            <Row
              key={option.id}
              ref={(el) => {
                rowRefs.current[index] = el;
              }}
              onMouseEnter={() => setSelectedIndex(index)}
              onMouseDown={(e) => {
                if (e.button !== 0) return;
                playClick();
                handleSelectOption(option.id);
              }}
              data-highlighted={
                index === selectedIndex && (allowMultiple || !otherActive)
              }
            >
              {needsExplicitSubmit ? (
                <ChoiceBadge data-selected={selected.has(option.id)}>
                  {selected.has(option.id) ? (
                    <Icon name="check" size={10} color="white" />
                  ) : (
                    <Text variant="text-small-md" color="var(--text-secondary)">
                      {index + 1}
                    </Text>
                  )}
                </ChoiceBadge>
              ) : (
                index < 9 && (
                  <KeyHint>
                    <Text variant="text-small-md" color="var(--text-secondary)">
                      {index + 1}
                    </Text>
                  </KeyHint>
                )
              )}
              <Text variant="text-regular" color="var(--text-primary)">
                {option.label}
              </Text>
              {index === selectedIndex &&
                !needsExplicitSubmit &&
                !otherActive && (
                  <Keyboard
                    style={{
                      position: "absolute",
                      right: 8,
                      top: 8,
                    }}
                  >
                    <Icon name="corner-down-left" size={12} />
                  </Keyboard>
                )}
            </Row>
          ))}
        {hasOptions && (
          <Row
            ref={(el) => {
              rowRefs.current[otherIndex] = el;
            }}
            onMouseEnter={() => setSelectedIndex(otherIndex)}
            onMouseDown={(e) => {
              if (e.button !== 0) return;
              playClick();
              handleActivateOther();
            }}
            data-highlighted={
              selectedIndex === otherIndex || (otherActive && !allowMultiple)
            }
          >
            {needsExplicitSubmit ? (
              <ChoiceBadge data-selected={otherActive}>
                {otherActive ? (
                  <Icon name="check" size={10} color="white" />
                ) : (
                  <Text variant="text-small-md" color="var(--text-secondary)">
                    {otherIndex + 1}
                  </Text>
                )}
              </ChoiceBadge>
            ) : (
              otherIndex < 9 && (
                <KeyHint>
                  <Text variant="text-small-md" color="var(--text-secondary)">
                    {otherIndex + 1}
                  </Text>
                </KeyHint>
              )
            )}
            {otherActive ? (
              <OtherInput
                ref={otherInputRef}
                placeholder="Something else…"
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
              />
            ) : (
              <Text
                variant="text-regular"
                color="var(--text-tertiary)"
                style={{ opacity: 0.4 }}
              >
                Something else…
              </Text>
            )}
            {selectedIndex === otherIndex &&
              !otherActive &&
              !needsExplicitSubmit && (
                <Keyboard
                  style={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                  }}
                >
                  <Icon name="corner-down-left" size={12} />
                </Keyboard>
              )}
          </Row>
        )}
        {isFreeform && (
          <FreeformGroup>
            {isRecording ? (
              <RecorderContainer>
                <VoiceRecorder
                  onTranscript={(text) => setFreeformText(text.trim())}
                  onCancel={() => setIsRecording(false)}
                  onSend={(text) => {
                    setIsRecording(false);
                    if (text.trim()) {
                      onAnswer({ text: text.trim() });
                    }
                  }}
                />
              </RecorderContainer>
            ) : (
              <InputWrapper>
                <FreeformInput
                  ref={inputRef}
                  placeholder="Type or record your answer…"
                  value={freeformText}
                  onChange={(e) => setFreeformText(e.target.value)}
                />
                <InputButtons>
                  {freeformText ? (
                    <Button
                      type="button"
                      icon="mic"
                      variant="secondary"
                      onMouseDown={(e) => {
                        if (e.button !== 0) return;
                        e.preventDefault();
                        setIsRecording(true);
                      }}
                    />
                  ) : (
                    <Tooltip content="Voice mode">
                      <Button
                        type="button"
                        icon="mic"
                        variant="secondary"
                        onMouseDown={(e) => {
                          if (e.button !== 0) return;
                          e.preventDefault();
                          setIsRecording(true);
                        }}
                      />
                    </Tooltip>
                  )}
                </InputButtons>
              </InputWrapper>
            )}
          </FreeformGroup>
        )}
      </PromptBody>
      <Footer>
        <Instructions>
          <FooterAction onPress={onDismiss}>
            <Text variant="text-smaller-md">Skip</Text>
            <Keyboard style={{ padding: "2px 4px" }}>
              <Text variant="text-smaller-md">esc</Text>
            </Keyboard>
          </FooterAction>
          <Icon name="slash" size={12} color="var(--border-alpha)" />
          <FooterAction onPress={onTypeInstead}>
            <Text variant="text-smaller-md">Chat instead</Text>
            <Keyboard
              style={{ padding: "2px 4px", gap: 2, display: "inline-flex" }}
            >
              <Text variant="text-smaller-md">⇧</Text>
              <Text variant="text-smaller-md">esc</Text>
            </Keyboard>
          </FooterAction>
        </Instructions>
        <Instructions>
          {step && (
            <>
              <Text variant="text-smaller-md">Cycle</Text>
              <Keyboard
                style={{ padding: "2px 4px", gap: 2, display: "inline-flex" }}
              >
                <Text variant="text-smaller-md">⌘</Text>
                <Text variant="text-smaller-md">⌃</Text>
                <Icon name="arrow-left" size={12} />
                <Text variant="text-smaller-md" style={{ opacity: 0.5 }}>
                  /
                </Text>
                <Icon name="arrow-right" size={12} />
              </Keyboard>
              <Icon name="slash" size={12} color="var(--border-alpha)" />
            </>
          )}
          {hasOptions && needsExplicitSubmit && (
            <>
              <Text variant="text-smaller-md">Select</Text>
              <Keyboard style={{ padding: "2px 4px" }}>
                <Text variant="text-smaller-md">space</Text>
              </Keyboard>
              <Icon name="slash" size={12} color="var(--border-alpha)" />
            </>
          )}
          <Text variant="text-smaller-md">
            {needsExplicitSubmit || otherActive ? "Submit" : "Select"}
          </Text>
          <Keyboard>
            <Icon name="corner-down-left" size={12} />
          </Keyboard>
        </Instructions>
      </Footer>
    </PromptContainer>
  );
}
